'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useLocale } from 'next-intl'

import type { BlogsApiItem, TagApiItem } from '@/lib/api/blogs'
import { BgImageOverlay } from '@/components/ui/bg-image-overlay'
import { Col } from '@/components/ui/col'

type Props = {
  blogs: BlogsApiItem[]
  filterTags?: TagApiItem[]
  basePath: string
  filterAllLabel?: string
  loadMoreLabel: string
}

const GRID_INITIAL_VISIBLE = 5
const GRID_LOAD_MORE_STEP = 6
const FALLBACK_COVER_IMAGE = '/images/photo-1460925895917-w1000.webp'

function CardCover({ blog }: { blog: BlogsApiItem }) {
  return (
    <div className="aspect-[16/9] relative overflow-hidden flex items-center justify-center">
      <BgImageOverlay
        src={blog.featured_image_url ?? FALLBACK_COVER_IMAGE}
        fallbackSrc={FALLBACK_COVER_IMAGE}
        className="editorial-cover"
      />
    </div>
  )
}

function formatDate(dateStr: string | null | undefined, locale: string): string {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function InsightsTechnologySolutionsLibraryGrid({
  blogs,
  basePath,
  loadMoreLabel,
}: Props) {
  const locale = useLocale()
  const [visibleCount, setVisibleCount] = useState(GRID_INITIAL_VISIBLE)

  const filtered = blogs
  const visibleBlogs = filtered.slice(0, visibleCount)
  const canLoadMore = filtered.length > visibleCount

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {visibleBlogs.map((blog) => (
          <Link
            key={blog.documentId}
            href={`${basePath}/${blog.slug}`}
            className="group bg-white border border-gray-200 flex flex-col h-full rounded-lg shadow-sm hover:shadow-md hover:border-gray-400 hover:-translate-y-1 active:translate-y-0 active:bg-gray-50 transition-all duration-300 overflow-hidden"
          >
            <CardCover blog={blog} />

            <Col className="p-8 flex-grow">
              {blog.tags?.[0] && (
                <span className="text-fs-sm font-mono font-bold text-charcoal-500 uppercase tracking-widest block mb-3">
                  {blog.tags[0].name}
                </span>
              )}
              <h3 className="text-xl font-bold text-charcoal-900 leading-snug mb-4 group-hover:text-brand-red transition-colors line-clamp-3">
                {blog.title}
              </h3>
              {blog.short_description && (
                <p className="text-sm text-charcoal-600 font-light leading-relaxed mb-6 flex-grow line-clamp-4">
                  {blog.short_description}
                </p>
              )}
              {blog.wp_post_date && (
                <span className="mt-auto block text-fs-xs text-charcoal-400 font-mono uppercase tracking-widest">
                  {formatDate(blog.wp_post_date, locale)}
                </span>
              )}
            </Col>
          </Link>
        ))}
        {canLoadMore && (
          <button
            type="button"
            onClick={() =>
              setVisibleCount((current) =>
                Math.min(current + GRID_LOAD_MORE_STEP, filtered.length),
              )
            }
            className="flex w-full flex-col items-center justify-center bg-gray-100 border border-gray-200 min-h-[300px] cursor-pointer rounded-lg transition-colors hover:bg-gray-200 active:bg-gray-300 group"
          >
            <span className="w-10 h-10 border border-charcoal-400 rounded-full flex items-center justify-center mb-4 group-hover:border-brand-red transition-colors">
              <span className="w-2 h-2 bg-charcoal-400 rounded-full group-hover:bg-brand-red transition-colors" />
            </span>
            <span className="text-xs font-bold text-charcoal-600 uppercase tracking-widest group-hover:text-brand-red transition-colors">
              {loadMoreLabel}
            </span>
          </button>
        )}
      </div>
    </>
  )
}
